import { useEffect, useRef, useState } from "react";
import { Slider, Tooltip } from "@material-tailwind/react";

import { setVolume, mute, unmute, rateItem, EventType } from '../../slices/local/orchestrator';
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { useRateSongMutation } from "../../slices/api/auth";
import { selectChannelState, selectConnection, selectPlayers, selectRatedSongs } from "../../state/selectors";

import volumeMute from '../../styles/assets/images/VolumeMute.svg';
import volumeLow from '../../styles/assets/images/VolumeLow.svg';
import volumeMedium from '../../styles/assets/images/VolumeMedium.svg';
import volumeHigh from '../../styles/assets/images/VolumeHigh.svg';

import dislike from '../../styles/assets/images/dislike.svg';
import like from '../../styles/assets/images/like.svg';
import megaphone from '../../styles/assets/images/megaphone.svg';
import { formatTime } from "../../helpers/format-time";
import { debounce } from "../../helpers/debounce";
import { getPlayer } from "../../middlewares/orchestrator";
import { useModal } from "../../contexts/modal";
import { VolumeRequestModal } from "../dialogs/VolumeRequestModal";
import { RateSongModal } from "../dialogs/RateSongModal";
import { Cube } from '../../players';
import { t } from 'i18next';
import SentryFeedbackButton from "../partials/SentryFeedbackButton";
import appConfig from "../../../app.config";
import { show as showTour } from '../../slices/local/tour';

const setVolumeDebounced = debounce(({ dispatch, playerId, channel, volume }) => {
  dispatch(setVolume({
    playerId,
    channel,
    volume
  }));
}, 20);

export const Volume = ({ playerId, channel }) => {
  const dispatch = useAppDispatch();
  const players = useAppSelector(selectPlayers);
  const connection = useAppSelector(selectConnection);
  const channelState = useAppSelector(state => selectChannelState(state, { playerId, channel })) || {};
  const [rateSong] = useRateSongMutation();
  const { muted, volume: channelVolume, queue } = channelState;
  const [volume, setTempVolume] = useState(channelVolume);
  const { showModal } = useModal();
  const [volumeRequest, setVolumeRequest] = useState(null);
  const [isVolumeTooltipOpen, setIsVolumeTooltipOpen] = useState(false)
  const [rateSongRequest, setRateSongRequest] = useState(null);
  const ratedSongs = useAppSelector(selectRatedSongs);
  const playlist = queue?.entries || [];
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressSeconds, setProgressSeconds] = useState(0);
  const channelStateRef = useRef(channelState);
  const progressRef = useRef({ percent: 0, seconds: 0 });
  let e = playlist.find(e => e.playing);
  if (players[playerId] !== undefined && players[playerId].isOnline && channelState.cubeEvents !== undefined && channelState.cubeEvents.length > 0 && players[playerId].type == Cube) {
    e = { ...channelState.cubeEvents[0].song, playing: true };
  }

  useEffect(() => {
    channelStateRef.current = channelState;
  }, [channelState]);

  useEffect(() => {
    const setProgress = ({ percent, seconds }) => {
      if (progressRef.current.percent === percent && progressRef.current.seconds === seconds) {
        return;
      }

      progressRef.current = { percent, seconds };
      setProgressPercent(percent);
      setProgressSeconds(seconds);
    };

    const interval = setInterval(() => {
      const currentChannelState = channelStateRef.current;
      const currentQueue = currentChannelState.queue;
      const currentPlaylist = currentQueue?.entries || [];
      const currentEntry = currentPlaylist.find(entry => entry.playing);

      if (currentEntry) {
        const playerProgress = getPlayer(currentChannelState, playerId, channel);
        if (playerProgress) {
          const progress = playerProgress.progress(currentQueue.playIndex);
          setProgress({
            percent: progress.percent,
            seconds: Math.round(progress.seconds)
          });
        }
      } else {
        setProgress({ percent: 0, seconds: 0 });
      }
    }, 500);

    return () => clearInterval(interval);
  }, [playerId, channel]);

  useEffect(() => {
    setTempVolume(channelState.volume);
  }, [channelState]);

  useEffect(() => {
    if (volumeRequest !== null) {
      showModal(VolumeRequestModal, {
        volume: volumeRequest.volume,
        onConfirm: () => {
          dispatch(setVolume({
            ...volumeRequest,
            defer: true
          }));

          setTempVolume(volumeRequest.volume);
        },
        onCancel: () => {
          setTempVolume(channelState.volume);
        }
      });
    }
  }, [volumeRequest]);

  useEffect(() => {
    if (rateSongRequest !== null && connection.available && player.isOnline && !ratedSongs.includes(rateSongRequest.songId)) {
      showModal(RateSongModal, {
        ...rateSongRequest,
        onConfirm: ({ comment }) => {
          rateSong({
            ...rateSongRequest,
            comment
          });

          dispatch(rateItem({
            playerId,
            channel,
            index: rateSongRequest.playIndex,
            songId: rateSongRequest.songId
          }));
        }
      })
    }
  }, [rateSongRequest])

  const triggerMuted = () => {
    if (muted) {
      dispatch(unmute({
        playerId,
        channel
      }));
    } else {
      dispatch(mute({
        playerId,
        channel
      }));
    }
  };

  const rate = like => {
    if (connection.available && player.isOnline) {
      let currentSong = playlist.find(e => e.playing);
      if (players[playerId].type == Cube) {
        currentSong = channelState.cubeEvents[0].song;
      }
      if (currentSong) {
        setRateSongRequest({
          songId: currentSong.persistentId,
          stationId: channelState.stationId,
          like,
          songName: `${e.artist} - ${e.title}`,
          playIndex: playlist.findIndex(e => e.playing)
        });
      }
    }
  };

  const changeVolume = e => {
    const player = getPlayer(channelState, playerId, channel);
    let volume = (parseInt(e.target.value) - 1) || 0;
    volume = Math.min(Math.max(volume, 0), 100);

    setTempVolume(volume);

    if (!player.requiresDeferredVolumeChange()) {
      // We decouple setting volumes to avoid a stuttering UI
      setVolumeDebounced({ dispatch, playerId, channel, volume });
    }
  };

  const postChangeVolume = e => {
    const player = getPlayer(channelState, playerId, channel);
    let volume = (parseInt(e.target.value) - 1) || 0;
    volume = Math.min(Math.max(volume, 0), 100);

    if (player.requiresDeferredVolumeChange()) {
      const channelSelector = {
        playerId,
        channel
      };
      channelSelector.volume = volume;
      setVolumeRequest(channelSelector);
    }
  };

  const getVolumeIcon = (volume) => {
    if (volume === 0) {
      return { volumeIconSrc: volumeMute, volumeTitle: 'Volume Mute' };
    }
    if (volume > 0 && volume <= 30) {
      return { volumeIconSrc: volumeLow, volumeTitle: 'Volume Low' };
    }
    if (volume > 30 && volume <= 60) {
      return { volumeIconSrc: volumeMedium, volumeTitle: 'Volume Medium' };
    }
    if (volume > 60 && volume <= 100) {
      return { volumeIconSrc: volumeHigh, volumeTitle: 'Volume High' };
    }
    return { volumeIconSrc: volumeMute, volumeTitle: 'Volume Mute' };
  };
  const { volumeIconSrc, volumeTitle } = getVolumeIcon(volume);

  const player = players[playerId];
  if (!player) {
    return <></>;
  }
  const numberOfPlayers = Object.values(players).length;
  
  return (
    <footer className="bg-yellow relative min-h-28 md:min-h-24 max-h-fit overflow-hidden">
      <div className="bg-dark h-1 w-full" style={{ transform: `translateX(${progressPercent}%)`, transition: 'transform 1s ease-out', willChange: 'transform' }}></div>

      <div className="mx-auto p-3 bg-light-blue h-full flex flex-col justify-center">
        <div className="flex flex-row items-center">
          <div className="flex-1 font-minion">
            {!connection.available && t('volume.no_network')}
            {e ? <>
              {t('station.playing')} <span className="font-bold select-all">{e.artist} - {e.title}</span> {players[playerId].type == Cube ? '' : <>({formatTime(progressSeconds)} / {formatTime(e.duration)})</>}
            </> : (player.isOnline ? t('volume.not_playing') : t('volume.player_offline'))}
          </div>
          <Tooltip content={volume} placement="top" open={isVolumeTooltipOpen} className={`hidden sm:block ${!connection.available || !player.isOnline || !player.allowVolumeChange ? 'grayscale' : ''}`}>
            <div className="flex flex-row items-center app-volume-bar"
              onMouseOver={() => setIsVolumeTooltipOpen(true)}
              onMouseLeave={() => setIsVolumeTooltipOpen(false)}
              onTouchStart={() => setIsVolumeTooltipOpen(true)}
              onTouchEnd={() => setIsVolumeTooltipOpen(false)}
            >
              <div className="pr-3">
                <img src={volumeIconSrc} alt={volumeTitle} title={volumeTitle} className={`h-9 block bg-yellow p-0.5 w-9 rounded-full ${muted || !connection.available || !player.isOnline || !player.allowVolumeChange ? 'grayscale' : ''}`} />
              </div>
              {player.allowVolumeChange && <div className={`pr-6 hidden sm:block ${!connection.available || !player.isOnline || !player.allowVolumeChange ? 'grayscale' : ''}`}>
                <Slider
                  onChange={connection.available && player.isOnline ? changeVolume : null}
                  onMouseUp={connection.available && player.isOnline ? postChangeVolume : null}
                  min={1} max={101}
                  value={(volume + 1) || 1}
                  size="sm"
                  barClassName="bg-yellow"
                  thumbClassName="[&::-moz-range-thumb]:bg-yellow [&::-webkit-slider-thumb]:bg-yellow"
                  trackClassName="[&::-webkit-slider-runnable-track]:bg-black [&::-moz-range-track]:bg-black"
                />
              </div>
              }
            </div>
          </Tooltip>
          <div>
            <img
              onClick={() => rate(false)}
              src={dislike}
              alt={t('station.dislike_song')}
              title={t('station.dislike_song')}
              className={`cursor-pointer w-9 h-9 ${!connection.available || !player.isOnline || !e || !e.playing || ratedSongs.includes(e.persistentId) ? 'grayscale pointer-events-none' : 'hover:brightness-110'}`}
            />
          </div>
          <div className="px-3">
            <img
              onClick={() => rate(true)}
              src={like}
              alt={t('station.like_song')}
              title={t('station.like_song')}
              className={`cursor-pointer h-9 ${!connection.available || !player.isOnline || !e || !e.playing || ratedSongs.includes(e.persistentId) ? 'grayscale pointer-events-none' : 'hover:brightness-110'}`}
            />
          </div>
          {appConfig.sentry.enabled && (
            <div className="app-report-issue-button">
              <div className="px-3">
                <SentryFeedbackButton className="flex md:hidden">
                  <img
                    onClick={() => rate(true)}
                    src={megaphone}
                    alt={t('feedback.triggerLabel')}
                    title={t('feedback.triggerLabel')}
                    className='cursor-pointer h-9'
                  />
                </SentryFeedbackButton>
              </div>
              <div>
                <SentryFeedbackButton className="hidden md:flex items-center justify-center bg-yellow border-black border-3 py-1.5 px-3 text-base font-semibold leading-7 text-dark hover:bg-black hover:text-yellow hover:border-yellow sm:text-sm sm:leading-6 text-nowrap" />
              </div>
            </div>
          )}
          {appConfig.showOnboarding && (numberOfPlayers == 1) && (
            <div className="px-3 web-tour-guide-button">
              <a className="flex items-center justify-center border-black bg-yellow border-3 py-1.5 px-3 text-base font-semibold leading-7 text-dark hover:bg-black hover:text-yellow hover:border-yellow sm:text-sm sm:leading-6 text-nowrap" role="button" onClick={() => dispatch(showTour())}>
                {t('footer.tour_guide')}
              </a>
            </div>
          )}
        </div>
        <div className="flex flex-col pt-2">
          {player.allowVolumeChange && (
            <div className={`flex flex-row items-center sm:hidden ${!connection.available || !player.isOnline || !player.allowVolumeChange ? 'grayscale' : ''}`}
              onMouseOver={() => setIsVolumeTooltipOpen(true)}
              onMouseLeave={() => setIsVolumeTooltipOpen(false)}
              onTouchStart={() => setIsVolumeTooltipOpen(true)}
              onTouchEnd={() => setIsVolumeTooltipOpen(false)}
            >
              <div className="flex-1 py-3">
                <Tooltip content={volume} placement="top" open={isVolumeTooltipOpen} className={`sm:hidden ${!connection.available || !player.isOnline || !player.allowVolumeChange ? 'grayscale' : ''}`}>
                  <Slider
                    onChange={connection.available && player.isOnline ? changeVolume : null}
                    onMouseUp={connection.available && player.isOnline ? postChangeVolume : null}
                    min={1} max={101}
                    value={(volume + 1) || 1}
                    size="md"
                    barClassName="bg-yellow"
                    thumbClassName="[&::-moz-range-thumb]:bg-yellow [&::-webkit-slider-thumb]:bg-yellow"
                    trackClassName="[&::-webkit-slider-runnable-track]:bg-black [&::-moz-range-track]:bg-black"
                  />
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
